import { FormEvent, useEffect, useState } from 'react'

import * as api from 'api/api'
import { AppSettings } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { SettingsTabs } from 'components/SettingsTabs'
import { Button, Card, ErrorState, Spinner, TextField } from 'components/ui'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { ForbiddenPage } from 'pages/system/ForbiddenPage'

/** Drives the real daily-report reminder cron (backend notificationService.scheduleReminders): an end-of-shift
 *  nudge to site engineers, then an escalation to PMs/admins if the report is still empty
 *  (Figma: Admin / 33 · Settings — Notifications & reminders). Channels (push/email/WhatsApp) are left out —
 *  only push notifications actually exist in this app today. */
export const NotificationSettingsPage = () => {
  const { user } = useAuth()
  const { data: settings, error, loading, reload } = useAsync(api.fetchAppSettings, [])
  const { busy, run } = useAction()
  const [form, setForm] = useState<AppSettings | null>(null)

  useEffect(() => {
    if (settings && !form) setForm(settings)
  }, [settings, form])

  if (!user?.isAdmin) return <ForbiddenPage message={t('إعدادات الإشعارات متاحة لمدير النظام فقط.', 'Notification settings are available to Admin only.')} />

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!form) return
    const done = await run('notifications', () => api.updateAppSettings(form), t('تم حفظ إعدادات التذكيرات', 'Reminder settings saved'))
    if (done) reload()
  }

  const organisationName = settings?.organisationName ?? 'JoPath'

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('الإعدادات', 'Settings')}</h1>
          <p className="muted">
            {t(`${organisationName} · التغييرات تُطبّق على كل مشروع وتُحفظ تلقائياً`, `${organisationName} · Changes apply to every project and are saved automatically`)}
          </p>
        </div>
      </div>

      <SettingsTabs />

      {loading ? (
        <Spinner />
      ) : error || !form ? (
        <ErrorState message={error ?? t('تعذر تحميل الإعدادات', 'Could not load settings')} onRetry={reload} />
      ) : (
        <Card title={t('تذكيرات التقارير اليومية', 'Daily report reminders')}>
          <p className="muted small">
            {t('متى يتم تذكير المهندسين، ومتى يتم إبلاغ مدير المشروع إن بقي التقرير فارغاً.', 'When to remind engineers, and when to tell the Project Manager if the report is still empty.')}
          </p>
          <form className="form-grid" onSubmit={save}>
            <TextField
              label={t('تذكير نهاية الدوام', 'End-of-shift reminder')}
              hint={t('يُرسل لمهندسي الموقع الذين لم يقدّموا تقرير اليوم بعد', "Sent to site engineers who have not submitted today's report")}
              type="time"
              value={form.reminderTime}
              onChange={(event) => setForm({ ...form, reminderTime: event.target.value })}
              required
            />
            <TextField
              label={t('تصعيد لمدير المشروع', 'Escalate to the Project Manager')}
              hint={t('إن بقي التقرير فارغاً، يتم إبلاغ مدير المشروع ومدير النظام', 'If the report is still empty, the Project Manager and Admin are notified')}
              type="time"
              value={form.escalationTime}
              onChange={(event) => setForm({ ...form, escalationTime: event.target.value })}
              required
            />
            <label className="checkbox span-2">
              <input
                type="checkbox"
                checked={form.remindOnFridays}
                onChange={(event) => setForm({ ...form, remindOnFridays: event.target.checked })}
              />
              {t('التذكير أيام الجمعة أيضاً', 'Also remind on Fridays')}
            </label>
            <div className="form-actions span-2">
              <Button type="submit" variant="primary" busy={busy === 'notifications'}>
                {t('حفظ', 'Save')}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  )
}
